import React, { useState, useContext } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import logo from "../../assets/logo.png";
import { API_BASE_URL } from "../api_config";
import { FiMail, FiLock, FiUser, FiX } from "react-icons/fi";
import { MdEmail, MdLock, MdVisibility, MdVisibilityOff } from "react-icons/md";

import "./login.css";
import UserContext from "../Authcontext";
import { Link, useNavigate } from "react-router-dom";

export default function Login() {
  const { setUser, setToken } = useContext(UserContext);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  
  // New states for profile selection
  const [showProfileSelection, setShowProfileSelection] = useState(false);
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);

  let navigate = useNavigate();
  const handleClose = () => {
    setShow(false);
    // Reset states when modal is closed
    setShowProfileSelection(false);
    setAvailableProfiles([]);
    setSelectedProfileId(null);
    setError("");
  };
  const handleShow = () => setShow(true);

  // Step 1: Check for multiple profiles (updated to match mobile app)
  const handleCheckMobile = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError("Please enter your mobile number or email");
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Use the same endpoint as mobile app for consistency
      const response = await axios.post(
        `${API_BASE_URL}/api/check-multiple-profiles`,
        { identifier: email },
        { withCredentials: true, timeout: 10000 }
      );
      
      console.log("Profile check response:", response.data);
      
      if (response.data.multipleProfiles && response.data.profiles.length > 1) {
        // Multiple profiles found
        setAvailableProfiles(response.data.profiles);
        setShowProfileSelection(true);
        setLoading(false);
      } else if (response.data.profiles && response.data.profiles.length === 1) {
        // Only one profile found
        setSelectedProfileId(response.data.profiles[0].id);
        setLoading(false);
      } else {
        // No profiles found
        setLoading(false);
        setError("No account found with this mobile number or email");
      }
    } catch (error) {
      console.error("Error checking profiles:", error);
      setLoading(false);
      
      if (error.response?.status === 400) {
        setError("Please enter a valid mobile number or email");
      } else {
        setError("An error occurred. Please try again.");
      }
    }
  };
  
  // Regular login without profile selection
  const handleRegularLogin = async (e) => {
    if (e) e.preventDefault();
    
    if (!password) {
      setError("Please enter your password");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/user-login`,
        {
          email,
          password,
        },
        { withCredentials: true }
      );
      
      handleLoginResponse(response);
    } catch (error) {
      console.error("Error during login:", error);
      setLoading(false);
      setError("Invalid email or password.");
    }
  };

  // Step 2: Handle profile selection
  const handleProfileSelect = (profileId) => {
    setSelectedProfileId(profileId);
    setShowProfileSelection(false);
  };

  // Step 3: Authenticate with selected profile
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!password) {
      setError("Please enter your password");
      return;
    }
    
    setLoading(true);

    try {
      // Use the same login approach as mobile app
      const response = await axios.post(
        `${API_BASE_URL}/api/user-login`,
        {
          email,
          password,
          profileId: selectedProfileId // Include profileId if available
        },
        { withCredentials: true }
      );
      
      handleLoginResponse(response);
    } catch (error) {
      console.error("Error during login:", error);
      setLoading(false);
      setError("An error occurred while logging in.");
    }
  };
  
  // Helper function to process login response
  const handleLoginResponse = (response) => {
    if (response.data.auth) {
      const userId = response.data.userId;
      setUser({ userId });
      setToken(response.data.token);
      Cookies.set("m_host-token", response.data.token, { expires: 1 / 24 });
      setLoading(false);

      if (response.data.verify === 1) {
        navigate("/", { 
          replace: true, 
          state: { userId }
        });
      } else {
        navigate("/user-under-verification", {
          replace: true,
          state: { userId },
        });
      }

      setEmail("");
      setPassword("");
      setShowProfileSelection(false);
      setSelectedProfileId(null);
    } else {
      setPassword("");
      setLoading(false);
      setError(response.data.error);
    }
  };

  // Reset to email input if user wants to use a different account
  const handleBackToEmailInput = () => {
    setShowProfileSelection(false);
    setSelectedProfileId(null);
    setEmail("");
  };

  return (
    <>
      <button onClick={handleShow} className="log-btn">
        Log In
      </button>

      <Modal
        show={show}
        onHide={handleClose}
        backdrop={true}
        keyboard={true}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="login-modal"
        size="sm"
      >
        
        <Modal.Header closeButton>
          <Modal.Title className="d-none">Login</Modal.Title>
        </Modal.Header>
        <Modal.Body className="compact-login-body">
          <div className="login-logo-wrapper">
            <div className="login-logo-container text-center">
              <img src={logo} alt="Tamil Matrimony" className="login-logo" />
            </div>
            
            <h2 className="login-welcome-text text-center">Welcome Back!</h2>
            <p className="login-subtitle text-center">Sign in to continue to your account</p>
          </div>
          
          {showProfileSelection ? (
            // Profile selection screen
            <div className="profile-selection">
              <h3 className="text-center mb-3">Select your profile</h3>
              <p className="text-center text-muted mb-4 small">Multiple profiles found with this mobile number</p>
              
              <div className="profile-list">
                {availableProfiles.map((profile) => (
                  <div 
                    key={profile.id} 
                    className={`profile-card ${selectedProfileId === profile.id ? 'selected' : ''}`}
                    onClick={() => handleProfileSelect(profile.id)}
                  >
                    <div className="profile-image">
                      {profile.image1 ? (
                        <img 
                          src={`${API_BASE_URL}/uploads/${profile.image1}`} 
                          alt={profile.name || 'Profile'} 
                          className="profile-pic"
                        />
                      ) : (
                        <div className="profile-placeholder">
                          <FiUser size={30} />
                        </div>
                      )}
                    </div>
                    <div className="profile-details">
                      <h4>{profile.name || 'Profile ' + profile.id}</h4>
                      <p>{profile.profile} seeking {profile.looking}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <button 
                type="button" 
                className="back-btn mt-3" 
                onClick={handleBackToEmailInput}
              >
                Use a different account
              </button>
              
              {selectedProfileId && (
                <div className="password-section mt-3">
                  <div className="form-group">
                    <label htmlFor="password">Enter Password</label>
                    <div className="input-with-icon">
                      <MdLock className="input-icon" />
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        value={password}
                        placeholder="Enter your password"
                        className="form-control"
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <span style={{marginTop: "-18px"}} className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                      </span>
                    </div>
                  </div>
                  
                  <button 
                    type="button" 
                    className="sign-in-btn mt-3" 
                    onClick={handleRegularLogin}
                    disabled={loading}
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </button>
                </div>
              )}
            </div>
          ) : (
            
            <div className="login-form">
              <div className="form-group1">
                <label htmlFor="email">Email or Mobile</label>
                <div className="input-with-icon">
                  <MdEmail className="input-icon" />
                  <input
                    type="text"
                    id="email"
                    value={email}
                    placeholder="Enter your email or mobile number"
                    className="form-control"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              
              {selectedProfileId ? (
                // Password entry for single profile
                <div className="form-group1">
                  <label htmlFor="password">Password</label>
                  <div className="input-with-icon">
                    <MdLock className="input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      placeholder="Enter your password"
                      className="form-control"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <span style={{marginTop: "-18px"}} className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </span>
                  </div>
                </div>
              ) : null}
              
              <Link to="/login/identify" className="forgot-pass">
                Forgot Password?
              </Link>
              
              <button 
                type="button" 
                className="sign-in-btn" 
                onClick={selectedProfileId || password ? handleRegularLogin : handleCheckMobile}
                disabled={loading}
              >
                {loading ? "Processing..." : (selectedProfileId || password ? "Sign In" : "Continue")}
              </button>
              
              {error && <div className="error-login">{error}</div>}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
